import { createClient } from '@/lib/supabase/client';

export interface Blog {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  featured_image: string | null;
  author_id: string | null;
  published_date: string | null;
  tags: string[] | null;
  read_time: number | null;
  status: 'draft' | 'published' | 'archived';
  created_at: string;
  updated_at: string;
  pdf_download_count: number;
}

export interface BlogFormData {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image: string;
  tags: string;
  read_time: string;
  status: 'draft' | 'published' | 'archived';
}

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

export const blogService = {
  async getAllBlogs() {
    const supabase = createClient();
    return supabase
      .from('blogs')
      .select('*')
      .order('created_at', { ascending: false });
  },

  async getPublishedBlogs() {
    const supabase = createClient();
    return supabase
      .from('blogs')
      .select('*')
      .eq('status', 'published')
      .order('published_date', { ascending: false });
  },

  async getBlogBySlug(slug: string) {
    const supabase = createClient();
    return supabase
      .from('blogs')
      .select('*')
      .eq('slug', slug)
      .eq('status', 'published')
      .maybeSingle();
  },

  async getBlogById(id: string) {
    const supabase = createClient();
    return supabase
      .from('blogs')
      .select('*')
      .eq('id', id)
      .maybeSingle();
  },

  async createBlog(data: BlogFormData) {
    const supabase = createClient();
    const tagsArray = data.tags
      ? data.tags.split(',').map(t => t.trim()).filter(t => t.length > 0)
      : [];

    return supabase.from('blogs').insert({
      title: data.title,
      slug: data.slug || generateSlug(data.title),
      excerpt: data.excerpt || null,
      content: data.content,
      featured_image: data.featured_image || null,
      tags: tagsArray.length > 0 ? tagsArray : null,
      read_time: data.read_time ? parseInt(data.read_time) : null,
      status: data.status,
      published_date: data.status === 'published' ? new Date().toISOString() : null,
    }).select().maybeSingle();
  },

  async updateBlog(id: string, data: BlogFormData) {
    const supabase = createClient();
    const tagsArray = data.tags
      ? data.tags.split(',').map(t => t.trim()).filter(t => t.length > 0)
      : [];

    return supabase.from('blogs').update({
      title: data.title,
      slug: data.slug || generateSlug(data.title),
      excerpt: data.excerpt || null,
      content: data.content,
      featured_image: data.featured_image || null,
      tags: tagsArray.length > 0 ? tagsArray : null,
      read_time: data.read_time ? parseInt(data.read_time) : null,
      status: data.status,
      published_date: data.status === 'published' ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    }).eq('id', id).select().maybeSingle();
  },

  async deleteBlog(id: string) {
    const supabase = createClient();
    return supabase.from('blogs').delete().eq('id', id);
  },

  async incrementPdfDownloadCount(blogId: string) {
    const supabase = createClient();
    return supabase.rpc('increment_pdf_download_count', { blog_id: blogId });
  },

  generateSlug,
};
